<template>
  <div class="min-h-screen bg-[#FAFAFA] pb-24">
    <div class="sticky top-0 z-10 bg-[#FAFAFA] px-4 pt-4 pb-3">
      <div class="flex items-center gap-3">
        <button
          class="text-gray-800 text-lg"
          aria-label="Back"
          type="button"
          @click="goBack"
        >
          <i class="fas fa-arrow-left" />
        </button>
        <h1 class="text-lg font-normal text-gray-900">
          Bookmarks
        </h1>
      </div>
    </div>

    <section
      v-if="!hasBookmarks"
      class="px-4 mt-10"
    >
      <div class="bg-white border border-dashed border-gray-300 rounded-3xl p-6 text-center text-gray-500">
        <i class="far fa-bookmark text-2xl text-gray-400 mb-3" />
        <p class="text-sm font-medium">
          No bookmarked reviews yet.
        </p>
        <p class="text-xs mt-2 text-gray-400">
          Bookmark a quiz review to revisit it quickly from here.
        </p>
      </div>
    </section>

    <section
      v-else
      class="px-4 mt-4 space-y-3"
    >
      <article
        v-for="item in formattedBookmarks"
        :key="item.startId"
        class="bg-white border border-gray-200 rounded-3xl p-4 shadow-sm transition hover:shadow-md"
      >
        <button
          type="button"
          class="w-full text-left"
          @click="openBookmark(item)"
        >
          <div class="flex justify-between items-start gap-3">
            <div class="flex-1 min-w-0">
              <h2 class="text-base font-semibold text-gray-900 truncate">
                {{ item.title }}
              </h2>
              <p class="text-xs text-gray-400 mt-1">
                {{ item.bookmarkedAtLabel }}
              </p>
            </div>
            <span
              v-if="item.requireReviewPin"
              class="text-[11px] font-medium text-[#2563EB] bg-blue-50 border border-blue-100 rounded-full px-2 py-[2px]"
            >
              PIN Required
            </span>
          </div>

          <div class="mt-4 grid grid-cols-2 gap-3 text-xs text-gray-600">
            <div class="flex items-center gap-2">
              <i class="fas fa-list-ol text-gray-400" />
              <span>{{ item.totalQuestions ?? 0 }} Questions</span>
            </div>
            <div class="flex items-center gap-2 justify-end">
              <i class="fas fa-check text-emerald-500" />
              <span>{{ formatCount(item.correctCount) }} Correct</span>
            </div>
            <div class="flex items-center gap-2">
              <i class="fas fa-times text-rose-500" />
              <span>{{ formatCount(item.wrongCount) }} Wrong</span>
            </div>
            <div
              v-if="item.query?.wrongOnly && item.query.wrongOnly !== 'false'"
              class="flex items-center gap-2 justify-end text-amber-600"
            >
              <i class="fas fa-exclamation-circle" />
              <span>Wrong answers only</span>
            </div>
          </div>
        </button>

        <div class="flex justify-end mt-4">
          <button
            type="button"
            class="text-xs font-medium text-red-500 px-3 py-1.5 rounded-full border border-red-200 hover:bg-red-50"
            @click.stop="removeBookmark(item.startId)"
          >
            Remove
          </button>
        </div>
      </article>
    </section>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useReviewBookmarkStore } from "@/stores/reviewBookmarkStore";

const router = useRouter();
const bookmarkStore = useReviewBookmarkStore();
bookmarkStore.hydrate();

const rawBookmarks = computed(() => bookmarkStore.bookmarkList ?? []);
const hasBookmarks = computed(() => rawBookmarks.value.length > 0);

const formattedBookmarks = computed(() =>
  rawBookmarks.value.map((item) => {
    const fallbackTitle = item?.startId
      ? `Quiz Review #${item.startId}`
      : "Quiz Review";
    const cleanTitle =
      item?.title && String(item.title).trim().length
        ? String(item.title).trim()
        : fallbackTitle;
    return {
      ...item,
      title: cleanTitle,
      bookmarkedAtLabel: formatDate(item.bookmarkedAt),
    };
  })
);

const goBack = () => router.back();

function formatDate(value) {
  if (!value) return "N/A";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return "N/A";
  }
}

function formatCount(value) {
  if (value === null || typeof value === "undefined") return "N/A";
  return Number.isFinite(Number(value)) ? Number(value) : value;
}

function sanitizeQuery(raw = {}) {
  return Object.fromEntries(
    Object.entries(raw).filter(
      ([key, value]) =>
        key !== "key" &&
        typeof value !== "undefined" &&
        value !== null &&
        String(value).length > 0
    )
  );
}

function openBookmark(item) {
  if (!item?.startId) return;
  const targetRoute = item.route || {};
  const target = {
    ...(targetRoute.name ? { name: targetRoute.name } : {}),
    ...(targetRoute.path ? { path: targetRoute.path } : {}),
    ...(targetRoute.params ? { params: { ...targetRoute.params } } : {}),
    query: sanitizeQuery(targetRoute.query ?? item.query ?? { startId: item.startId }),
  };

  if (!target.path && !target.name) {
    target.path = `/quiz-review/${item.startId}`;
  }

  if (!target.query.startId) {
    target.query.startId = item.startId;
  }

  router.push(target);
}

function removeBookmark(startId) {
  bookmarkStore.removeBookmark(startId);
}
</script>
